// Lightweight shared Ask/View session. sessionStorage is intentionally bounded;
// the current view lens carries cumulative intent so old turns can be dropped.

const KEY = 'ask_session:v1';
const MAX_TURNS = 12;
const MAX_SOURCE_HISTORY = 18;

// The route a visitor was on when they entered a View page. Module-level so it
// survives SPA navigation without touching storage; reset on full reload, where
// the origin is unknown and the fallback label applies.
let viewOriginRoute = '';

export function setViewOriginRoute(route) {
  viewOriginRoute = String(route || '').split(/[?#]/)[0];
}

export function getViewOriginRoute() {
  return viewOriginRoute;
}

// The page a visitor came from when they jumped into the current page through a
// link (any route change that is not a header tab or a back/forward traversal).
// Module-level so it survives SPA navigation; a full reload resets it, and then
// the floating back control is hidden because the origin is unknown.
let siteOrigin = null;
let tabNavigationPending = false;
let pendingCollectionReturn = null;

// Header tabs (and the brand mark) are top-level navigation, so arriving
// through them must not leave a back control behind. The click handler marks
// the next navigation; the router guard consumes the flag.
export function markTabNavigation() {
  tabNavigationPending = true;
}

export function consumeTabNavigation() {
  const pending = tabNavigationPending;
  tabNavigationPending = false;
  return pending;
}

export function setSiteOrigin(origin) {
  siteOrigin = origin
    ? {
        route: String(origin.route || ''),
        label: String(origin.label || ''),
        scrollY: Number(origin.scrollY) || 0,
        collectionState: origin.collectionState || null,
        viewportAnchor: origin.viewportAnchor || null,
        askReturn: origin.askReturn || null,
      }
    : null;
}

export function getSiteOrigin() {
  return siteOrigin;
}

export function clearSiteOrigin() {
  siteOrigin = null;
}

export function prepareCollectionReturn(state) {
  pendingCollectionReturn = state
    ? {
        mode: String(state.mode || ''),
        openId: String(state.openId || ''),
      }
    : null;
}

export function consumeCollectionReturn() {
  const value = pendingCollectionReturn;
  pendingCollectionReturn = null;
  return value;
}

// Scroll position to restore when a View page finishes loading after the
// visitor returns to it via the "Back to View" control. Module-level so it
// survives the SPA navigation; consumed (and cleared) once the View mounts.
let pendingViewReturnScroll = null;

export function setPendingViewReturnScroll(y) {
  pendingViewReturnScroll = Number(y) || 0;
}

export function consumePendingViewReturnScroll() {
  const value = pendingViewReturnScroll;
  pendingViewReturnScroll = null;
  return value;
}

function read() {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function write(session) {
  if (typeof window === 'undefined' || !window.sessionStorage) return session;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    // Private mode or storage quota. The in-memory copy still works for this page.
  }
  if (typeof window.dispatchEvent === 'function' && typeof CustomEvent !== 'undefined') {
    window.dispatchEvent(new CustomEvent('ask-session-updated', { detail: session }));
  }
  return session;
}

export function createAskSession(origin = null) {
  return {
    version: 1,
    turns: [],
    source_history: [],
    currentView: null,
    currentShare: null,
    origin: origin || { route: '/' },
  };
}

export function loadAskSession() {
  const session = read();
  if (!session || session.version !== 1) return createAskSession();
  if (!Array.isArray(session.turns)) session.turns = [];
  return session;
}

export function saveAskSession(session) {
  if (!session) return null;
  const bounded = {
    ...session,
    turns: (session.turns || []).slice(-MAX_TURNS),
  };
  return write(bounded);
}

export function addAskTurn(session, turn) {
  const turnSourceIds = Array.isArray(turn.source_ids) ? turn.source_ids.slice(0, 80) : [];
  const sourceHistory = [...(session.source_history || []), ...turnSourceIds]
    .map((id) => String(id || ''))
    .filter(Boolean)
    .filter((id, index, values) => values.lastIndexOf(id) === index)
    .slice(-MAX_SOURCE_HISTORY);
  const next = {
    ...session,
    source_history: sourceHistory,
    turns: [...(session.turns || []), {
      role: turn.role === 'assistant' ? 'assistant' : 'user',
      text: String(turn.text || '').slice(0, 1200),
      source_ids: turnSourceIds,
      at: Date.now(),
    }],
  };
  return saveAskSession(next);
}

export function setAskView(session, view) {
  const next = { ...session, currentView: view || null, currentShare: null };
  return saveAskSession(next);
}

export function setAskShare(session, share) {
  const next = { ...session, currentShare: share || null };
  return saveAskSession(next);
}

export function setAskOrigin(session, route) {
  const next = { ...session, origin: { route: route || '/' } };
  return saveAskSession(next);
}

// Recent turns used to carry Ask context into a guided View.
export function recentAskTurns(session, limit = 6) {
  return (session?.turns || []).slice(-limit).map((turn) => ({
    role: turn.role,
    text: turn.text,
    source_ids: turn.source_ids || [],
  }));
}

export function askSourceIdsInRecent(session, limit = 6) {
  const ids = [...(session?.source_history || [])];
  for (const turn of (session?.turns || []).slice(-limit)) {
    for (const id of turn.source_ids || []) ids.push(id);
  }
  return [...new Set(ids)].slice(-MAX_SOURCE_HISTORY);
}

// A View build for the exact same question, asked within the last three days,
// reuses the existing composed View instead of regenerating it. Returns the
// stored View when a match exists, otherwise null.
const DUPLICATE_VIEW_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

export function recentDuplicateViewQuestion(question, session = loadAskSession()) {
  const q = String(question || '').trim().toLowerCase();
  if (!q) return null;
  const now = Date.now();
  const askedRecently = (session?.turns || []).some((turn) => (
    turn.role === 'user'
    && String(turn.text || '').trim().toLowerCase() === q
    && Number(turn.at) > 0
    && now - Number(turn.at) <= DUPLICATE_VIEW_WINDOW_MS
  ));
  if (!askedRecently) return null;
  const current = session?.currentView;
  if (!current || String(current.question || '').trim().toLowerCase() !== q) return null;
  return current;
}

// A completed View belongs to the question it was composed for.
export function matchingAskView(question, session) {
  const q = String(question || '').trim().toLowerCase();
  const view = session?.currentView;
  return q && view && String(view.question || '').trim().toLowerCase() === q ? view : null;
}
